import { safeLocalStorage } from '@toss/storage';

const FALLBACK_FP_STORAGE_KEY = 'zep.quiz.ai.fingerprint-id';

let fingerprintPromise: Promise<string | null> | null = null;
let memoryFallbackFingerprintId: string | null = null;

function createFallbackFingerprintId() {
  // 이미 존재 가드 + 폴백이 있어 웹뷰에서도 throw 하지 않는다. quiz-app 은
  // @zep/utils 표면을 최소로 유지(배럴 drag 회피)하므로 로컬 구현을 유지한다.
  /* eslint-disable zep/no-restricted-syntax -- guarded fallback; quiz-app keeps minimal @zep/utils surface */
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  /* eslint-enable zep/no-restricted-syntax */
  return `fallback-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getStoredFallbackFingerprintId(): string | null {
  try {
    return safeLocalStorage.get(FALLBACK_FP_STORAGE_KEY);
  } catch {
    return null;
  }
}

function saveFallbackFingerprintId(value: string): void {
  try {
    safeLocalStorage.set(FALLBACK_FP_STORAGE_KEY, value);
  } catch {
    // In restricted browser contexts, an in-memory fallback still keeps the
    // current page session identifiable for quota requests.
  }
}

function getFallbackFingerprintId(): string {
  if (!memoryFallbackFingerprintId) {
    memoryFallbackFingerprintId = createFallbackFingerprintId();
  }

  saveFallbackFingerprintId(memoryFallbackFingerprintId);
  return memoryFallbackFingerprintId;
}

async function getFingerprintIdFromLibrary() {
  if (typeof window === 'undefined') return null;

  try {
    const { default: FingerprintJS } = await import(
      '@fingerprintjs/fingerprintjs'
    );
    const fpAgent = await FingerprintJS.load();
    const fpResult = await fpAgent.get();
    return fpResult.visitorId ?? null;
  } catch {
    return null;
  }
}

export async function getClientFingerprintId() {
  if (typeof window === 'undefined') return null;

  const storedFallbackId = getStoredFallbackFingerprintId();
  if (storedFallbackId) return storedFallbackId;

  if (!fingerprintPromise) {
    fingerprintPromise = getFingerprintIdFromLibrary();
  }

  const libraryFpId = await fingerprintPromise;
  if (libraryFpId) {
    saveFallbackFingerprintId(libraryFpId);
    return libraryFpId;
  }

  return getFallbackFingerprintId();
}
